<template>
  <div class="card min-h-40 lg:min-h-44 flex flex-col justify-between"
       :class="{ 'opacity-50': isUnavailable }"
       :data-testid="isTravelBuddy ? 'weekgoal-travelbuddy' : 'weekgoal-card'"
       :data-goal-type="type"
       :data-completed="String(completed)">
    <div class="w-full">
      <h3 class="font-bold" data-testid="weekgoal-title">{{ displayTitle }}</h3>
    </div>
    <div class="flex sm:flex-row flex-col gap-2 justify-between">
      <div class="flex flex-col gap-y-1">
        <div class="flex flex-row items-center gap-x-2">
          <img
            class="h-10 w-10 block rounded-full"
            :src="getURL"
            alt=""
            @error="onImageError"
          />
          <h4
            v-if="isTravelBuddy && travelBuddyName"
            data-testid="weekgoal-travelbuddy-name"
          >{{ travelBuddyName }}</h4>
        </div>
        <h4>{{ xp }} {{ $t('HomeComponent.XP') }}</h4>
      </div>
      <div class="flex sm:flex-col-reverse flex-row-reverse justify-between gap-2 items-center">
        <Button :label="buttonText" @click="getButtonAction"
          class="px-3 py-1 text-base text-white mx-auto rounded-full"
          :class="buttonStyle" :disabled="buttonDisabled || isUnavailable"
          data-testid="weekgoal-action"
          v-tooltip="isUnavailable ? unavailabilityMessage : ''" unstyled />
        <div class="flex justify-center"><label>
            {{ progress+'/'+requiredCompletions }}</label></div>
      </div>
    </div>
  </div>
  <FeedbackOverlayComponent v-if="showFeedbackDialog" :visible="showFeedbackDialog"
    @update:visible="showFeedbackDialog = $event" :data="notification"></FeedbackOverlayComponent>
  <TravelBuddyExpandedCardComponent :visible="showCard"
    @update:visible="showCard = $event"></TravelBuddyExpandedCardComponent>
  <TravelBuddyFeedbackOverlayComponent v-if="provideFeedback" :visible="provideFeedback"
    @update:visible="provideFeedback = $event"  @close-parent="handleFeedbackClose">
  </TravelBuddyFeedbackOverlayComponent>
</template>
<script>
import defaultProfileImage from '@/assets/defaultProfileImage.js';

import { Button } from 'primevue';
import FeedbackOverlayComponent from '../Shared/FrontendOverlays/FeedbackOverlayComponent.vue';
import TravelBuddyExpandedCardComponent from './TravelBuddyExpandedCardComponent.vue';
import TravelBuddyFeedbackOverlayComponent from '../Shared/FrontendOverlays/TravelBuddyFeedbackOverlayComponent.vue';
import { getFeatureDisplayNameOrFallback } from '@/utils/featureDisplay';

export default {
  name: 'WeekgoalComponent',
  components: {
    Button,
    FeedbackOverlayComponent,
    TravelBuddyExpandedCardComponent,
    TravelBuddyFeedbackOverlayComponent,
  },
  props: {
    id: {
      type: [String, Number],
      required: true,
    },
    title: {
      type: [String, Object],
      required: true,
    },
    img: {
      type: String,
      default: '',
    },
    xp: {
      type: Number,
      required: true,
    },
    assessmentType: {
      type: String,
      required: false,
    },
    type: {
      type: String,
      required: true,
    },
    completed: {
      type: Boolean,
      required: true,
    },
    progress: {
      type: Number,
      required: true,
    },
    requiredCompletions: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      showCard: false,
      showFeedbackDialog: false,
      notification: {},
      provideFeedback: false,
      imageLoadError: false,
    };
  },
  computed: {
    featureName() {
      return getFeatureDisplayNameOrFallback(
        this.$store,
        'travelBuddyHistory',
        this.$t('FallbackTexts.travelBuddyHistory'),
        this.$i18n?.locale,
      );
    },
    featureNameDevTip() {
      return getFeatureDisplayNameOrFallback(
        this.$store,
        'developmentTip',
        this.$t('FallbackTexts.developmentTip'),
        this.$i18n?.locale,
      );
    },
    displayTitle() {
      // Handle both old string format and new object format for backward compatibility
      if (typeof this.title === 'string') {
        return this.title;
      }
      // Use German title if current locale is German and title.de exists,
      // otherwise use English title
      if (this.$i18n.locale === 'de' && this.title?.de) {
        return this.title.de;
      }
      return this.title?.en || this.title?.title || '';
    },
    getRefreshValue() {
      return this.$store.getters.getRefreshValue;
    },
    getURL() {
      if (this.imageLoadError) {
        return defaultProfileImage;
      }
      if (this.isTravelBuddy) {
        // Done level goal: no partner identity (cannot reliably show who completed it).
        if (this.completed) {
          const hasImgDone = this.img && String(this.img).trim() !== '';
          if (hasImgDone) {
            return this.img;
          }
          return defaultProfileImage;
        }
        const photo = this.travelBuddyMeetingInfo?.teammatePhoto;
        if (photo) {
          return `${photo}?_${this.getRefreshValue}`;
        }
        const hasImg = this.img && String(this.img).trim() !== '';
        if (hasImg) {
          return this.img;
        }
        return defaultProfileImage;
      }
      const hasImg = this.img && String(this.img).trim() !== '';
      if (hasImg) {
        return this.img;
      }
      return defaultProfileImage;
    },
    isTravelBuddy() {
      return this.type === 'travelBuddy';
    },
    travelBuddyMeetingInfo() {
      const travelBuddyMeetingInfo = this.$store.getters.getTravelBuddyMeetingInfo;
      if (travelBuddyMeetingInfo.meetingThisWeek) {
        return travelBuddyMeetingInfo;
      }
      return false;
    },
    /**
     * Partner name on Level Goals only while the TB goal is still open.
     * When Done we hide the name: previousTravelBuddies / current meeting cannot
     * reliably identify who completed the level goal (C vs later B vs open A).
     */
    travelBuddyName() {
      if (this.isTravelBuddy && this.completed) {
        return '';
      }
      if (!this.isUnavailable) {
        return this.travelBuddyMeetingInfo?.teammateFull || '';
      }
      return '';
    },
    travelBuddyStatus() {
      return this.$store.getters.getTravelBuddyMeetingStatus;
    },
    hasTravelBuddyFeedbackSubmitted() {
      const entry = this.travelBuddyMeetingInfo?.myFeedbackEntry
        || this.travelBuddyMeetingInfo?.meetingInfo?.meetingFeedback?.find(
          (e) => String(e.user) !== String(this.travelBuddyMeetingInfo?.teammateID),
        );
      return Boolean(entry?.feedback?.trim?.());
    },
    buttonText() {
      if (this.isUnavailable) {
        return this.$t('WeekgoalComponent.unavailable');
      }

      if (this.completed) return this.$t('WeekgoalComponent.done');

      if (this.type === 'travelBuddy') {
        // if a meeting exists, then button text can be start, done or provide feedback
        if(this.travelBuddyMeetingInfo) { 
          if (this.travelBuddyStatus === 'created') return this.$t('WeekgoalComponent.open');
          if (!this.hasTravelBuddyFeedbackSubmitted) return this.$t('TravelBuddyFeedbackOverlayComponent.provideFeedback');
        } 
        // if no meeting exists, then button text should be done
        // unavailable is already checked at the top
        return this.$t('WeekgoalComponent.done');        
      }
      
      // if (this.completed) return this.$t('WeekgoalComponent.done');
      // if (this.progress === 0) return this.$t('WeekgoalComponent.open');
      return this.$t('WeekgoalComponent.open');
    },
    buttonStyle() {
      if (this.isUnavailable) {
        return 'bg-[var(--color-disabled)]';
      }

      if (this.completed) return 'bg-[var(--color-disabled)] disabled:cursor-not-allowed';

      if (this.type === 'travelBuddy') {
        // active if meeting exists and is not completed or skipped
        const isActive = this.travelBuddyMeetingInfo && (this.travelBuddyStatus === 'created' || !this.hasTravelBuddyFeedbackSubmitted);
        return isActive ? 'btn-primary-accent' : 'bg-[var(--color-disabled)] disabled:cursor-not-allowed';
      }

      return 'btn-primary-accent px-3 py-1.5 sm:px-2 sm:py-1 w-full sm:w-fit mx-0 sm:mx-auto text-sm sm:text-base mt-1';
      // return this.completed ? 'bg-[var(--color-disabled)] disabled:cursor-none' : 'btn-primary-accent px-3 py-1.5 sm:px-2 sm:py-1 w-full sm:w-fit mx-0 sm:mx-auto text-sm sm:text-base mt-1';
    },
    buttonDisabled() {
      if (this.isUnavailable) {
        return true;
      }
      // Level goal completed is terminal for all types, including travelBuddy.
      if (this.completed) {
        return true;
      }
      if (this.type === 'travelBuddy') {
        return ['completed', 'skipped'].includes(this.travelBuddyStatus)
          && this.hasTravelBuddyFeedbackSubmitted;
      }

      return false;
    },
    isUnavailable() {
      switch (this.type) {
        case 'devTip':
          return this.unavailableDevTip;
        case 'travelBuddy':
          return this.unavailableTravelBuddy;
        case 'setProfilePicture':
        case 'createWorkshop':
        case 'createPersonalGoal':
        case 'achievePersonalGoal':
        case 'createCollaborationTracker':
          return false;
        case 'workshopReflection':
          return this.unavailableWorkshopReflection;
        default:
          return this.unavailableAssessment;
      }
    },
    unavailableDevTip() {
      const availableTips = this.$store.getters.getDevTippOverview.filter((e) => !e.done);
      return availableTips.length === 0 && !this.completed;
    },
    unavailableTravelBuddy() {
      return !this.travelBuddyMeetingInfo && !this.completed;
    },
    unavailableWorkshopReflection() {
      return !this.findOpenWorkshopReflectionAssessment() && !this.completed;
    },
    unavailableAssessment() {
      const availableAssessments = this.$store.getters.getAllAssessments
        .filter((e) => e.type === this.assessmentType
        && (e.status === 'open' || e.status === 'began'));
      return availableAssessments.length === 0 && !this.completed;
    },
    unavailabilityMessage() {
      switch (this.type) {
        case 'devTip':
          return this.$t('WeekgoalComponent.errors.noDevTipsAvailable', {text: this.featureNameDevTip});
        case 'travelBuddy':
          return this.$t('WeekgoalComponent.errors.noTravelBuddyMeeting', {text: this.featureName});
        default:
          return this.$t('WeekgoalComponent.errors.noAssessmentAvailable');
      }
    },
  },
  watch: {
    travelBuddyMeetingInfo: {
      handler(newValue) {
        if (newValue) {
          console.log('Travel buddy info loaded:', newValue);
        }
      },
      immediate: true,
    },
    img() {
      this.imageLoadError = false;
    },
  },
  methods: {
    onImageError() {
      this.imageLoadError = true;
    },
    getButtonAction() {
      switch (this.type) {
        case 'devTip':
          // get the latest tip that has not been completed
          // eslint-disable-next-line no-case-declarations
          const tip = this.$store.getters.getDevTippOverview.filter((e) => !e.done)[0];
          this.notification = {
            helpful: tip?.positive,
            text: tip?.text,
            tipId: tip?.tip_id,
            feedback_text: tip?.feedback_text,
            done: tip?.done,
          };
          this.showFeedbackDialog = true;
          break;
        case 'travelBuddy':
          this.getTravelBuddyFlow();
          break;
        case 'setProfilePicture':
          this.$router.push('/edit-profile');
          break;
        case 'createWorkshop':
          this.$store.commit('setSelectedCreateTab', 'workshops');
          this.$store.commit('addFrontendOverlay', { id: Math.random(), type: 'taskCreator' });
          break;
        case 'workshopReflection':
          this.openWorkshopReflectionAssessment();
          break;
        case 'createPersonalGoal':
          this.$store.commit('setSelectedCreateTab', 'personalGoals');
          this.$store.commit('addFrontendOverlay', { id: Math.random(), type: 'taskCreator' });
          break;
        case 'achievePersonalGoal':
          this.$router.push('/personal-goals');
          break;
        case 'createCollaborationTracker':
          this.$store.commit('setSelectedCreateTab', 'kssTracker');
          this.$store.commit('addFrontendOverlay', { id: Math.random(), type: 'taskCreator' });
          break;
        default:
          // assessments
          this.goToAssessment();
          break;
      }
    },
    findOpenWorkshopReflectionAssessment() {
      const assessments = this.$store.getters.getAllAssessments || [];
      return assessments.find(
        (e) => String(e.type || '').toLowerCase() === 'workshop'
          && (e.status === 'open' || e.status === 'began'),
      ) || null;
    },
    async openWorkshopReflectionAssessment() {
      try {
        await this.$store.dispatch('getAssessmentsOverview');
      } catch (e) {
        // Use cached assessments if refresh fails
      }
      const workshopAssessment = this.findOpenWorkshopReflectionAssessment();
      if (!workshopAssessment?._id) {
        return;
      }
      // Same routing as TaskComponent for open assessments
      if (workshopAssessment.displayIntroPage === true) {
        this.$router.push(`/assessment-intro?id=${workshopAssessment._id}`);
      } else {
        this.$router.push(`/questionnaire?id=${workshopAssessment._id}`);
      }
    },
    // check if user has an assessment available and link it
    goToAssessment() {
      const assessmentId = this.$store.getters.getAllAssessments
        .filter((e) => e.type === this.assessmentType
        && (e.status === 'open' || e.status === 'began'))[0]._id;
      this.$router.push(`/questionnaire?id=${assessmentId}`);
    },
    getTravelBuddyFlow() {
      // Completed level goal must not reopen the meeting card or feedback overlay.
      if (this.completed || this.buttonDisabled) {
        return;
      }
      if (this.travelBuddyStatus !== 'completed') {
        this.showCard = true;
      } else {
        this.provideFeedback = true;
      }
    },
    handleFeedbackClose() {
      // Close the feedback dialog
      this.provideFeedback = false;
    },
  },
};
</script>
